import type { Map as MapLibreMap, RasterTileSource } from 'maplibre-gl'

import type { Layer, RefreshOutcome } from '../layer'

/**
 * The Iowa State Mesonet radar fallback: a real XYZ tile cache, unlike
 * nowCOAST, but its history is exposed as twelve *relative* offsets
 * (`nexrad-n0q-900913-m05m` … `-m55m`, probed 2026-09-16; `-m60m` is a 404)
 * rather than absolute timestamps.
 *
 * Two consequences, both deliberate:
 *
 * - The frame timestamps this layer reports are computed from the clock at
 *   load time, not authoritative. They are close enough to label a loop and
 *   not close enough to trust for anything else.
 * - These URLs repeat while their content changes, so they must never get a
 *   `CacheFirst` service-worker rule. See vite.config.ts. For the same reason
 *   every URL carries the 5-minute boundary it was asked at as `?_=`: IEM
 *   sends `max-age=300`, so a refresh re-asking the same URL could be handed
 *   the browser's copy of a picture that is now a frame old. IEM ignores the
 *   parameter (checked 2026-09-23: same bytes with and without).
 */

const STEP_MINUTES = 5
/** Offsets in minutes, oldest first: 55 … 5, then 0 (the unsuffixed layer). */
const OFFSETS = [55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0]

/** Snapped to the clock's last 5-minute boundary, so the labels line up with
 *  how IEM actually publishes. */
function framesAt(now: number): Date[] {
  const base = now - (now % (STEP_MINUTES * 60_000))
  return OFFSETS.map((m) => new Date(base - m * 60_000))
}

export interface XyzFramesOptions {
  id: string
  /** e.g. https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0 */
  base: string
  /** e.g. nexrad-n0q-900913 */
  product: string
  attribution: string
  opacity: number
}

export class XyzFramesLayer implements Layer {
  readonly id: string
  private frames: Date[] = []
  private map: MapLibreMap | null = null
  private current = OFFSETS.length - 1
  private opacity: number
  private visible = true

  constructor(private readonly opts: XyzFramesOptions) {
    this.id = opts.id
    this.opacity = opts.opacity
  }

  get times(): readonly Date[] {
    return this.frames
  }

  currentTime(): Date | null {
    // Computed from the wall clock at load time, not authoritative: IEM's
    // offsets are relative to whenever you ask.
    return this.frames[this.current] ?? null
  }

  load(): Promise<void> {
    this.frames = framesAt(Date.now())
    this.current = this.frames.length - 1
    return Promise.resolve()
  }

  /**
   * Relabel the loop from the clock and re-ask every tile. The URLs are
   * relative offsets, so the sources stay where they are and only their
   * cache-busting stamp moves; `setTiles` drops what each source holds.
   */
  async refresh(canPatch: () => boolean): Promise<RefreshOutcome> {
    const next = framesAt(Date.now())
    if (this.frames.length && next[0].getTime() === this.frames[0].getTime()) return 'unchanged'
    const live = this.current === this.frames.length - 1
    this.frames = next
    if (live) this.current = this.frames.length - 1
    const map = this.map
    if (!map) return 'patched'
    if (!canPatch()) return 'remount'
    this.frames.forEach((_, i) => {
      const source = map.getSource(this.key(i)) as RasterTileSource | undefined
      source?.setTiles?.([this.url(i)])
    })
    return 'patched'
  }

  private key(i: number): string {
    return `wx:${this.id}:${i}`
  }

  private url(i: number): string {
    const offset = OFFSETS[i]
    const suffix = offset === 0 ? '' : `-m${String(offset).padStart(2, '0')}m`
    const stamp = Math.floor((this.frames[this.frames.length - 1]?.getTime() ?? 0) / 1000)
    return `${this.opts.base}/${this.opts.product}${suffix}/{z}/{x}/{y}.png?_=${stamp}`
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    this.frames.forEach((_, i) => {
      const key = this.key(i)
      if (map.getLayer(key)) return
      map.addSource(key, {
        type: 'raster',
        tiles: [this.url(i)],
        tileSize: 256,
        attribution: this.opts.attribution,
      })
      map.addLayer(
        {
          id: key,
          type: 'raster',
          source: key,
          layout: { visibility: this.visible ? 'visible' : 'none' },
          paint: {
            'raster-opacity': i === this.current ? this.opacity : 0,
            'raster-fade-duration': 0,
          },
        },
        beforeId,
      )
    })
  }

  remove(map: MapLibreMap): void {
    this.frames.forEach((_, i) => {
      const key = this.key(i)
      if (map.getLayer(key)) map.removeLayer(key)
      if (map.getSource(key)) map.removeSource(key)
    })
    if (this.map === map) this.map = null
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    this.paint(this.current, opacity)
  }

  setTime(time: Date): void {
    let next = this.current
    let best = Infinity
    this.frames.forEach((t, i) => {
      const delta = Math.abs(t.getTime() - time.getTime())
      if (delta < best) {
        best = delta
        next = i
      }
    })
    if (next === this.current) return
    this.paint(next, this.opacity)
    this.paint(this.current, 0)
    this.current = next
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    const map = this.map
    if (!map) return
    this.frames.forEach((_, i) => {
      const key = this.key(i)
      if (map.getLayer(key)) map.setLayoutProperty(key, 'visibility', visible ? 'visible' : 'none')
    })
  }

  ready(): Promise<void> {
    const map = this.map
    if (!map) return Promise.resolve()
    return new Promise((resolve) => map.once('idle', () => resolve()))
  }

  private paint(i: number, value: number): void {
    const key = this.key(i)
    if (this.map?.getLayer(key)) this.map.setPaintProperty(key, 'raster-opacity', value)
  }
}
